import { Box, Divider, FormControl, MenuItem, Select, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import type { FieldArrayWithId } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import { PRODUCT_TEST_DOCUMENT_TYPES } from "../constants"
import type { ProductTestDocumentFormValues, ProductTestFormValues } from "../schemas"
import { getProductTestDocumentTypeLabel } from "../utils"

import { ProductTestDocumentsTable } from "./ProductTestDocumentsTable/ProductTestDocumentsTable"

import {
  ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC,
  IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
} from "@/components/upload/constants"
import FileInput from "@/components/upload/FileInput"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import type { FieldGroupEnum, SignedUrl } from "@/graphql/codegen/graphql"

interface ProductTestDocumentsFormProps {
  title: string
  fields: FieldArrayWithId[]
  productTestDocuments?: SignedUrl[]
  onDelete?: (document: SignedUrl) => void
}

export const ProductTestDocumentsForm: FC<ProductTestDocumentsFormProps> = ({
  title,
  fields,
  productTestDocuments = [],
  onDelete = () => {},
}) => {
  const { control } = useFormContext<{ documents: ProductTestDocumentFormValues[] }>()

  return (
    <Box component='fieldset'>
      <legend className='font-medium tracking-[0.2px] text-gray-700'>{title}</legend>
      <Divider className='mb-6 mt-1' />
      {!fields.length && !productTestDocuments.length && <Typography>No documents found</Typography>}
      {productTestDocuments.length > 0 && (
        <ProductTestDocumentsTable data={productTestDocuments} canDownload={false} onDelete={onDelete} />
      )}
      {fields.map((item, index) => (
        <Box key={item.id}>
          {productTestDocuments.length > 0 && index === 0 && <Divider className='my-6' />}
          <Box className='mb-6 flex gap-6'>
            <Controller
              name={`documents.${index}.type`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl className='flex-1'>
                  <label id='document-type-label' className={FIELD_LABEL_CLASSES}>
                    Document Type<span className='text-red-600'>*</span>
                  </label>
                  <Select
                    {...field}
                    {...SELECT_STYLING_PROPS}
                    labelId='document-type-label'
                    error={!!error}
                    renderValue={(selected) =>
                      selected ? (
                        getProductTestDocumentTypeLabel(selected as FieldGroupEnum)
                      ) : (
                        <span className='text-gray-500'>Select Document Type</span>
                      )
                    }
                    data-test='document-type-select'
                  >
                    {PRODUCT_TEST_DOCUMENT_TYPES.map((value) => (
                      <MenuItem key={value} data-test='document-type-option' value={value}>
                        {getProductTestDocumentTypeLabel(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && (
                    <p className={FIELD_ERROR_CLASSES} data-testid='document-type-error'>
                      {error.message}
                    </p>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name={`documents.${index}.description`}
              control={control}
              render={({ field }) => (
                <Box className='flex-1'>
                  <label htmlFor='document-description' className={FIELD_LABEL_CLASSES}>
                    Description
                  </label>
                  <TextField
                    {...field}
                    {...TEXT_FIELD_STYLING_PROPS}
                    id='document-description'
                    placeholder='Enter Description'
                    data-test='document-description-input'
                  />
                </Box>
              )}
            />
          </Box>
          <FileInput<ProductTestFormValues>
            name={`documents.${index}.file`}
            allowedMimeTypes={IMAGE_MIME_TYPES_WITH_PDF_AND_DOC}
            allowedTypesLabel={ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC}
          />
          {index !== fields.length - 1 && <Divider className='my-6' />}
        </Box>
      ))}
    </Box>
  )
}
